import classNames from "classnames";
import { type HousingProduct, HousingProductType, type KITProductData } from "~/domains/product/types/ProductData";
import { Translation } from "~/app/i18n/Translation";
import { multiply } from "~/utils/PriceUtils";
import { useSelector } from "react-redux";
import { getNetPrice, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { Price, PriceType } from "~/domains/pricing/components/Price/v1/Price";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { type WishlistOptionalPartModel } from "~/features/wishlist/types/model";
import { type CellContext, type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";

import { styled } from "~/common/mui";

const Title = styled("div")(({ theme }) => ({
  ...theme.typography.titleMedium,
  fontWeight: "bold",
  marginBottom: theme.spacing(4),
}));
import { Trans, useTranslation } from "react-i18next";
import {
  SIZE_BELOW_PHONE,
  SIZE_DESKTOP_L,
  SIZE_DESKTOP_M,
  SIZE_DESKTOP_S,
  SIZE_PHONE,
  SIZE_TABLET,
  type SupportedScreenSizes,
  useNumericScreenSize,
} from "~/common/hooks/useNumericScreenSize";
import { NetPrice, type NetPriceProps } from "~/features/productDetails/components/ProductSummary/components/NetPrice";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

interface IncludedTableProps {
  className?: string;
  product: KITProductData;
  quantity: number;
  priceReference: string;
}
interface OptionalTableProps extends IncludedTableProps {
  className?: string;
  wishlistOptionalParts: WishlistOptionalPartModel[];
}

interface ItemRow {
  product: ProductDescriptor;
  hideDesignation: boolean;
  quantity: number;
}
type ColumnMaker = (size: SupportedScreenSizes) => ColumnDef<ItemRow>;

const DESIGNATION_COLUMN: ColumnMaker = (size) => ({
  id: "product",
  accessorKey: "product",
  header: () => <Trans i18nKey={"wishlistPage.table.header.designation"} />,
  cell: (cell: CellContext<ItemRow, unknown>) => {
    const { product, hideDesignation } = cell.row.original;
    const designation = product.base.designation;
    const type = product.base.shortDescription;
    return (
      <div>
        <div className={classNames(!hideDesignation && "f-bold")}>
          {hideDesignation ? <Translation {...type} /> : designation}
        </div>
        {!hideDesignation && (
          <div className="label-large">
            <Translation {...type} />
          </div>
        )}
      </div>
    );
  },
  size: DESIGNATION_SIZES[size],
});

type SizeMap = Record<SupportedScreenSizes, number | undefined>;
const DESIGNATION_SIZES: SizeMap = {
  [SIZE_PHONE]: 108 + 8,
  [SIZE_TABLET]: 16 + 192 + 12,
  [SIZE_DESKTOP_S]: 16 + 192 + 12,
  [SIZE_DESKTOP_M]: 16 + 192 + 12,
  [SIZE_DESKTOP_L]: 16 + 192 + 12,
};

const QUANTITY_COLUMN: ColumnMaker = (size) => ({
  id: "quantity",
  accessorKey: "quantity",
  header: () => <Trans i18nKey={"wishlistPage.table.header.qty"} />,
  cell: (cell: CellContext<ItemRow, unknown>) => {
    return cell.renderValue();
  },
  size: QUANTITY_SIZES[size],
});
const QUANTITY_SIZES: SizeMap = {
  [SIZE_PHONE]: 52 + 8,
  [SIZE_TABLET]: 12 + 192 + 12,
  [SIZE_DESKTOP_S]: 12 + 192 + 12,
  [SIZE_DESKTOP_M]: 12 + 192 + 12,
  [SIZE_DESKTOP_L]: 12 + 192 + 12,
};
const PRICE_COLUMN: ColumnMaker = (size) => ({
  id: "netPrice",
  accessorKey: "netPrice",
  header: () => <Trans i18nKey={"cartPage.table.header.price.title"} />,
  cell: (cell: CellContext<ItemRow, unknown>) => {
    const data = cell.row.original;
    return <NetPrice packCode={""} product={data.product} quantity={data.quantity} />;
  },
  size: PRICE_SIZES[size],
});
const PRICE_SIZES: SizeMap = {
  [SIZE_PHONE]: 48 + 8,
  [SIZE_TABLET]: 12 + 124 + 12,
  [SIZE_DESKTOP_S]: 12 + 124 + 12,
  [SIZE_DESKTOP_M]: 12 + 124 + 12,
  [SIZE_DESKTOP_L]: 12 + 124 + 12,
};

const TOTAL_PRICE_COLUMN: ColumnDef<ItemRow> = {
  id: "totalPrice",
  accessorKey: "totalPrice",
  header: () => <Trans i18nKey={"wishlistPage.table.header.total"} />,
  cell: (cell: CellContext<ItemRow, unknown>) => {
    const { quantity, product } = cell.row.original;
    return <TotalNetPrice packCode={""} product={product} quantity={quantity} />;
  },
  size: undefined,
};

type ColumnListMaker = (size: SupportedScreenSizes) => ColumnDef<ItemRow>[];
const includedColumns: ColumnListMaker = (size) => [
  DESIGNATION_COLUMN(size),
  {
    ...QUANTITY_COLUMN(size),
    size: undefined,
  },
];

const optionalColumns: ColumnListMaker = (size) => [
  DESIGNATION_COLUMN(size),
  QUANTITY_COLUMN(size),
  PRICE_COLUMN(size),
  TOTAL_PRICE_COLUMN,
];

export const IncludedPartsTable = (props: IncludedTableProps) => {
  const tableData = transformIncludedData(props.product, props.quantity, props.priceReference);

  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  if (screenSize === SIZE_BELOW_PHONE) {
    return null;
  }

  return (
    <div className={classNames("scha__wishlist__included-parts-table", props.className)}>
      <Title>{t("page.cart.housing.included.title")}</Title>
      <TanStackTable data={tableData} columns={includedColumns(screenSize)} fullwidth={true} />
    </div>
  );
};
export const OptionalPartsTable = (props: OptionalTableProps) => {
  const tableData = transformOptionalData(
    props.product,
    props.quantity,
    props.priceReference,
    props.wishlistOptionalParts,
  );

  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  if (screenSize === SIZE_BELOW_PHONE) {
    return null;
  }

  return (
    <div className={classNames("scha__wishlist__optional-parts-table", props.className)}>
      <Title>{t("page.cart.housing.optional.title")}</Title>
      <TanStackTable data={tableData} columns={optionalColumns(screenSize)} fullwidth={true} />
    </div>
  );
};

const transformIncludedData = (parentProduct: KITProductData, quantity: number, priceReference: string) => {
  const transformItem = (item: HousingProduct): ItemRow => ({
    quantity: item.quantity * quantity,
    product: ProductService.useProductDescriptor({ productCode: item.product.code, priceReference }),
    hideDesignation: item.type === HousingProductType.HOUSING,
  });
  return parentProduct.includedParts.map(transformItem);
};

const transformOptionalData = (
  parentProduct: KITProductData,
  quantity: number,
  priceReference: string,
  wishlistOptionalParts: WishlistOptionalPartModel[],
) => {
  const transformItem = (item: HousingProduct): ItemRow => ({
    quantity: item.quantity * quantity,
    product: ProductService.useProductDescriptor({
      productCode: item.product.code,
      priceReference,
      options: { fetch: true },
    }),
    hideDesignation: false,
  });

  const selectedCodes = new Set(wishlistOptionalParts.map((_) => _.productCode));

  return parentProduct.optionalParts.filter((item) => selectedCodes.has(item.product.code)).map(transformItem);
};

export const TotalNetPrice = (props: NetPriceProps) => {
  const classes = props.netPriceClasses;
  const classesOnRequest = props.netPriceOnRequestClasses ?? props.netPriceClasses;
  const totalQuantity =
    props.quantity +
    useSelector(
      selectProperAmountInCart({
        productCode: props.product.base.code,
        packCode: "",
        priceReference: props.product.priceReference,
      }),
    );
  const price = getNetPrice({ ...props, totalQuantity });
  const finalPrice = price ? multiply(price, props.quantity / price.unit) : price;
  if (isProductPMA(props.product)) {
    return null;
  }
  const clss = !price?.value || price?.value <= 0 ? classesOnRequest : classes;
  return <Price variant={PriceType.Default} price={finalPrice} classes={clss} />;
};
